// Presentation architecture only: not a surveyed room, HVAC design or electrical
// installation. The FEEDER equipment and its control domains remain separate.
export const productionRoomLayout = Object.freeze({
  floorLevel: -1.55,
  rearWallZ: -8.4,
  rightWallX: 8.4,
  wallTop: 10.05,
  floorSize: 80,
});

export function createProductionRoom(THREE) {
  const root = new THREE.Group(); root.name = "feeder-indoor-production-room";
  root.userData = { presentationOnly: true, surveyed: false, environment: "indoor-production" };
  const geometries = new Set(), materials = new Set();
  const { floorLevel, rearWallZ, rightWallX, wallTop, floorSize } = productionRoomLayout;
  const height = wallTop - floorLevel;
  const surface = (name, color, options = {}) => {
    const value = new THREE.MeshStandardMaterial({ color, roughness: .62, metalness: 0, ...options });
    value.name = name; materials.add(value); return value;
  };
  const wallMaterial = surface("production-warm-white-wall", 0xe3e5df, { roughness: .75 });
  const floorMaterial = surface("production-seamless-epoxy-floor", 0xa7b2b1, { roughness: .46, metalness: .04 });
  const seamMaterial = surface("production-panel-joint", 0xb7bfbc, { roughness: .8 });
  const trimMaterial = surface("production-satin-metal-trim", 0xb5c0bd, { roughness: .42, metalness: .58 });
  const grilleMaterial = surface("production-ventilation-grille", 0xd1d8d5, { roughness: .46, metalness: .46 });
  const darkMaterial = surface("production-ventilation-interior", 0x485757, { roughness: .94 });
  const mesh = (name, geometry, material, position, parent = root) => {
    geometries.add(geometry);
    const value = new THREE.Mesh(geometry, material); value.name = name;
    value.position.set(...position); value.receiveShadow = true;
    // Room architecture never enters equipment selection or measurement flows.
    value.userData = { presentationOnly: true };
    parent.add(value); return value;
  };
  const box = (name, size, material, position, parent) => mesh(name, new THREE.BoxGeometry(...size), material, position, parent);

  // Continuous resin floor. No garden texture, paving pattern or floating base.
  const floor = mesh("production-continuous-floor", new THREE.PlaneGeometry(floorSize, floorSize), floorMaterial, [0, floorLevel, 0]);
  floor.rotation.x = -Math.PI / 2;

  // The two open sides face the initial inspection camera. Wall panels are
  // intentionally single-sided, so orbiting outside does not hide the equipment
  // behind a solid wall. This is a cutaway presentation, not a sealed cleanroom.
  const rearWall = mesh("production-rear-wall", new THREE.PlaneGeometry(32, height), wallMaterial, [rightWallX - 16, floorLevel + height / 2, rearWallZ]);
  const rightWall = mesh("production-right-wall", new THREE.PlaneGeometry(32, height), wallMaterial, [rightWallX, floorLevel + height / 2, rearWallZ + 16]);
  rightWall.rotation.y = -Math.PI / 2;
  const ceiling = mesh("production-removable-ceiling", new THREE.PlaneGeometry(32, 32), wallMaterial, [rightWallX - 16, wallTop, rearWallZ + 16]);
  ceiling.rotation.x = Math.PI / 2; ceiling.visible = false;

  // Restrained flush panel joints establish scale without a busy grid.
  const joints = [];
  for (let x = rightWallX - 28; x < rightWallX; x += 4) {
    joints.push(mesh("production-rear-panel-joint", new THREE.PlaneGeometry(.018, height), seamMaterial, [x, floorLevel + height / 2, rearWallZ + .003]));
  }
  for (let z = rearWallZ + 4; z < rearWallZ + 32; z += 4) {
    const seam = mesh("production-side-panel-joint", new THREE.PlaneGeometry(.018, height), seamMaterial, [rightWallX - .003, floorLevel + height / 2, z]);
    seam.rotation.y = -Math.PI / 2; joints.push(seam);
  }

  // A smooth floor-to-wall cove is modeled rather than painted into an image.
  const coveSection = new THREE.Shape();
  coveSection.moveTo(0, 0); coveSection.lineTo(.19, 0);
  coveSection.quadraticCurveTo(.04, .015, 0, .19); coveSection.lineTo(0, 0);
  const coveGeometry = new THREE.ExtrudeGeometry(coveSection, { depth: 32, steps: 1, bevelEnabled: false, curveSegments: 8 });
  const rearCove = mesh("production-rear-hygienic-cove", coveGeometry, floorMaterial, [rightWallX, floorLevel, rearWallZ]);
  rearCove.rotation.y = -Math.PI / 2;
  const rightCove = mesh("production-side-hygienic-cove", coveGeometry, floorMaterial, [rightWallX, floorLevel, rearWallZ]);
  rightCove.scale.x = -1;

  const rearSkirting = box("production-rear-wall-skirt", [32, .16, .025], trimMaterial, [rightWallX - 16, floorLevel + .26, rearWallZ + .018]);
  const sideSkirting = box("production-side-wall-skirt", [.025, .16, 32], trimMaterial, [rightWallX - .018, floorLevel + .26, rearWallZ + 16]);

  // Closed, flush service chase above the equipment. No exposed mains wiring or
  // suggestion that the separate FEEDER/Chaac supplies share a control circuit.
  const serviceChase = box("production-closed-overhead-service-chase", [19.8, .24, .16], wallMaterial, [-1.7, 7.45, rearWallZ + .085]);
  box("production-service-chase-lower-seam", [19.8, .013, .005], seamMaterial, [-1.7, 7.35, rearWallZ + .168]);
  const serviceDrop = box("production-closed-service-drop", [.16, 3.65, .14], wallMaterial, [6.9, 5.5, rearWallZ + .075]);
  box("production-closed-service-cover", [.56, .76, .13], wallMaterial, [6.9, 3.6, rearWallZ + .09]);

  // Architectural supply/return grilles. They are scenery, not a claim that
  // airflows, hygiene class, circulation rates or cooling have been validated.
  const vents = [];
  const slatGeometry = new THREE.BoxGeometry(2.35, .037, .075); geometries.add(slatGeometry);
  const matrix = new THREE.Matrix4();
  [-4.6, 3.7].forEach((x, index) => {
    const vent = new THREE.Group(); vent.name = `production-vent-${index + 1}`;
    vent.userData = { presentationOnly: true, validatedAirflow: false };
    root.add(vent); vents.push(vent);
    box("production-vent-frame", [2.66, .91, .11], grilleMaterial, [x, 6.15, rearWallZ + .065], vent);
    box("production-vent-dark-recess", [2.42, .69, .012], darkMaterial, [x, 6.15, rearWallZ + .127], vent);
    const slats = new THREE.InstancedMesh(slatGeometry, grilleMaterial, 9); slats.name = "production-vent-satin-louvers";
    slats.userData = { presentationOnly: true }; slats.receiveShadow = true;
    for (let row = 0; row < 9; row += 1) {
      matrix.makeTranslation(x, 6.15 - .28 + row * .07, rearWallZ + .16);
      slats.setMatrixAt(row, matrix);
    }
    slats.instanceMatrix.needsUpdate = true; vent.add(slats);
  });

  let disposed = false;
  return {
    root, floor, rearWall, rightWall, ceiling, joints, rearCove, rightCove, rearSkirting, sideSkirting, serviceChase, serviceDrop, vents,
    setRoofVisible(visible) { ceiling.visible = visible === true; },
    dispose() {
      if (disposed) return;
      disposed = true;
      geometries.forEach(geometry => geometry.dispose());
      materials.forEach(material => material.dispose());
      root.clear();
    },
  };
}
